import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useId } from "react";
import GfgButton from "@/components/ui/GfgButton";
import { COPY } from "@/constants/campaignContacts";
import { resolveScheduleTimeZone, scheduleZoneLabel } from "@/utils/timezone-helper";
import { sendTimeOptions } from "./addRecipients-helper";
import { maxSendDateInput, minSendDateInput, zonedDateInput } from "./campaignContacts-helper";
import StepNotice from "./StepNotice";
import type { SendTimeCampaign, SendTimeChoice, SendTimeMode, SendTimeOption } from "./types";

interface AddRecipientSendTimeStepProps {
  campaign: SendTimeCampaign;
  choice: SendTimeChoice;
  onPick: (mode: SendTimeMode) => void;
  onDate: (date: string) => void;
  onTime: (time: string) => void;
  /** Bulk automation: one send date for every undated row (the options do not apply). */
  batchDate?: { value: string; onChange: (date: string) => void };
  /** The payment settled on the previous step, or null when there was no payment step. */
  recap: { headline: string; detail: string } | null;
  /** "Change": back to the payment step, the send-time choice kept (R16). */
  onChange?: () => void;
  /** Why the current choice can't be confirmed. */
  error: string | null;
}

const OPTION_ICONS: Record<SendTimeMode, typeof SendOutlinedIcon> = {
  now: SendOutlinedIcon,
  with_campaign: EventOutlinedIcon,
  later: ScheduleOutlinedIcon,
};

const LABEL_CLASS =
  "block mb-1.5 font-lato text-[12px] font-semibold text-graphite uppercase tracking-[0.8px]";

/** The prototype's `.cx-pay-summary`: what step 1 settled, with a way back to change it. */
function PaymentRecap({
  recap,
  onChange,
}: {
  recap: { headline: string; detail: string };
  onChange?: () => void;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-smoke bg-clear-dust px-3.5 py-2.5">
      <CheckCircleOutlineIcon
        sx={{ fontSize: 18, flexShrink: 0, color: "var(--color-dust-ink)" }}
      />
      <div className="min-w-0 flex-1">
        <strong className="block text-[13.5px] text-rodeo-black">{recap.headline}</strong>
        {recap.detail && (
          <small className="mt-0.5 block text-[12px] text-graphite">{recap.detail}</small>
        )}
      </div>
      {onChange && (
        <GfgButton type="button" variant="text" className="text-dust-ink" onClick={onChange}>
          {COPY.addWizard.change}
        </GfgButton>
      )}
    </div>
  );
}

/** One send-time choice — a toggle button, as the prototype's `.cx-amend-opt`. */
function SendOptionButton({
  option,
  active,
  onPick,
}: {
  option: SendTimeOption;
  active: boolean;
  onPick: (mode: SendTimeMode) => void;
}) {
  const Icon = OPTION_ICONS[option.mode];
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onPick(option.mode)}
      className={[
        "flex cursor-pointer items-start gap-2 rounded-lg border bg-white px-3 py-2.5 text-left",
        "font-lato text-rodeo-black transition-colors",
        active
          ? "border-rodeo-black shadow-[inset_0_0_0_1px_var(--color-rodeo-black)]"
          : "border-smoke hover:border-rodeo-dust",
      ].join(" ")}
    >
      <Icon sx={{ fontSize: 18, flexShrink: 0, color: "var(--color-dust-ink)" }} />
      <span className="min-w-0">
        <strong className="block text-[13px]">{option.label}</strong>
        <small className="mt-0.5 block text-[11.5px] text-graphite">{option.sub}</small>
      </span>
    </button>
  );
}

/** A labelled native date/time input, styled as the recipient form's send date. */
function PickerField({
  id,
  label,
  type,
  value,
  onChange,
  min,
  max,
  errorId,
}: {
  id: string;
  label: string;
  type: "date" | "time";
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  errorId?: string;
}) {
  return (
    <div className="min-w-0 flex-1">
      <label htmlFor={id} className={LABEL_CLASS}>
        {label} *
      </label>
      <input
        id={id}
        type={type}
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={errorId !== undefined}
        aria-describedby={errorId}
        className={[
          "w-full h-10.5 px-3.5 border rounded-btn bg-white",
          "font-lato text-[16px] text-rodeo-black outline-none transition-colors",
          errorId
            ? "border-good-wine focus:border-good-wine"
            : "border-smoke focus:border-dark-dust",
        ].join(" ")}
      />
    </div>
  );
}

/** Names the campaign's zone: the dates and times below are read in it. */
function ZoneNote({ zone }: { zone: string }) {
  return (
    <small className="block w-full text-[12px] text-graphite">
      {COPY.addWizard.sendOptions.zoneNote(scheduleZoneLabel(zone))}
    </small>
  );
}

/**
 * When the added recipients' gift email goes out (B7): Scheduled → with the campaign / later;
 * Active → now / later; a bulk automation picks one date for its undated rows instead.
 */
export default function AddRecipientSendTimeStep({
  campaign,
  choice,
  onPick,
  onDate,
  onTime,
  batchDate,
  recap,
  onChange,
  error,
}: AddRecipientSendTimeStepProps) {
  const id = useId();
  const T = COPY.addWizard.sendOptions;
  const options = sendTimeOptions(campaign);
  const errorId = error === null ? undefined : `${id}-error`;
  const zone = resolveScheduleTimeZone(campaign.scheduledTz);
  // The close instant's calendar day in the campaign's zone is the "later" picker's ceiling (a time
  // before the close still fits that day); a batch send date stops the day before, as the API refuses
  // the close day itself.
  const closeDate = zonedDateInput(campaign.expireDate, zone);

  return (
    <div className="flex flex-col gap-4">
      <span className={LABEL_CLASS}>{T.heading}</span>
      {recap && <PaymentRecap recap={recap} onChange={onChange} />}
      {options.length > 0 && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {options.map((option) => (
            <SendOptionButton
              key={option.mode}
              option={option}
              active={choice.mode === option.mode}
              onPick={onPick}
            />
          ))}
        </div>
      )}
      {options.length > 0 && choice.mode === "later" && (
        <div className="flex flex-wrap gap-3">
          <ZoneNote zone={zone} />
          <PickerField
            id={`${id}-date`}
            label={T.date}
            type="date"
            value={choice.date}
            onChange={onDate}
            max={closeDate}
            errorId={errorId}
          />
          <PickerField
            id={`${id}-time`}
            label={T.time}
            type="time"
            value={choice.time}
            onChange={onTime}
            errorId={errorId}
          />
        </div>
      )}
      {batchDate && (
        <>
          <ZoneNote zone={zone} />
          <PickerField
            id={`${id}-batch`}
            label={T.batchDate}
            type="date"
            value={batchDate.value}
            onChange={batchDate.onChange}
            min={minSendDateInput(zone)}
            max={maxSendDateInput(campaign.expireDate, zone)}
            errorId={errorId}
          />
        </>
      )}
      {error !== null && <StepNotice id={errorId}>{error}</StepNotice>}
    </div>
  );
}
